
import { firstValueFrom } from 'rxjs'

import Backdrop from '@mui/material/Backdrop'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Input from '@mui/material/Input'
import AdminPage from 'admin/components/AdminPage'
import { adminGetSubscription, searchApps, updateApp } from 'admin/services/apps/net'
import { DatePicker } from 'components/DatePicker'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import * as React from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { TIMESTAMP_MULTIPLIER } from 'utils/format/date'
import styles from './AppsRoute.pcss'
import { AppCurrency, AppInfo, DEFAULT_CURRENCY, DEFAULT_TIER, INR_TIER } from './AppTableRow'

const TIER_OPTIONS: PPSelectOptions = {
  1: { label: '1' },
  2: { label: '2' },
  3: { label: '3' }
}

const CURRENCY_OPTIONS: PPSelectOptions = {
  usd: { label: 'USD' },
  inr: { label: 'INR' }
}

const INR_TIER_HINT = 'INR accounts are always tier 3.'

function resolveTier(app: AppInfo) {
  if ((app.currency ?? DEFAULT_CURRENCY) === 'inr') {
    return INR_TIER
  }
  return app.tier ?? DEFAULT_TIER
}

export function AppDetailsRoute() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const passedApp = (location.state as { app?: AppInfo } | null)?.app ?? null
  const [app, setApp] = React.useState<AppInfo | null>(passedApp)
  const [loading, setLoading] = React.useState(false)
  const [renewalChanged, setRenewalChanged] = React.useState(false)
  const [subscription, setSubscription] = React.useState<any>(null)
  const profilesInputRef = React.useRef<HTMLInputElement>(null)
  const postsInputRef = React.useRef<HTMLInputElement>(null)
  const stripeInputRef = React.useRef<HTMLInputElement>(null)

  // The app's product handle is passed in rather than read from state - on a direct visit `app` is still null here
  const fetchSubscription = React.useCallback((stripeId: string, productHandle?: string) => {
    firstValueFrom(dispatch(adminGetSubscription(stripeId)))
      .then(subResponse => {
        const items: any[] = subResponse.subscription?.items.data || []
        const handleOf = (item: any): string | undefined => item?.price?.product?.metadata?.handle
        // The plan item - add-on items sit next to it on the same subscription
        const sub = (productHandle && items.find(item => handleOf(item)?.startsWith(productHandle)))
          || items.find(item => !handleOf(item)?.startsWith('addon-'))
        setSubscription(sub || null)
        const periodEnd = sub?.currentPeriodEndActual
        if (periodEnd) {
          const nextRenewal = new Date(periodEnd * TIMESTAMP_MULTIPLIER).toISOString()
          setApp(current => current ? ({ ...current, nextRenewal }) : null)
        }
      })
      .catch(error => {
        console.error('Failed to fetch subscription:', error)
      })
      .finally(() => setLoading(false))
  }, [dispatch])

  React.useEffect(() => {
    if (!id) {
      return
    }
    if (passedApp && String(passedApp.id) === id) {
      if (passedApp.stripeSubscriptionId && !passedApp.nextRenewal) {
        setLoading(true)
        fetchSubscription(passedApp.stripeSubscriptionId, passedApp.product)
      }
      return
    }
    setLoading(true)
    firstValueFrom(dispatch(searchApps(id)))
      .then((response: any) => {
        const match = Array.isArray(response)
          ? response.find((a: any) => String(a.id) === id || String(a.userId) === id) || response[0]
          : null
        if (!match) {
          dispatch(message('App not found!'))
          setLoading(false)
          navigate('/admin/support/apps', { replace: true })
          return
        }
        const next: AppInfo = {
          ...match,
          isBeta: match.isBeta === 1 ? '1' : '0',
          masterAdmin: match.masterAdmin === 1 ? '1' : '0'
        }
        setApp(next)
        if (next.stripeSubscriptionId) {
          fetchSubscription(next.stripeSubscriptionId, next.product)
        } else {
          setLoading(false)
        }
      })
      .catch(() => {
        dispatch(message('Error fetching app!'))
        setLoading(false)
      })
  }, [id, dispatch, navigate, passedApp, fetchSubscription])

  React.useEffect(() => {
    if (app) {
      if (profilesInputRef.current) {
        profilesInputRef.current.value = app.profilesLimit + ''
      }
      if (postsInputRef.current) {
        postsInputRef.current.value = app.postsPerDay + ''
      }
      if (stripeInputRef.current) {
        stripeInputRef.current.value = app.stripeSubscriptionId || ''
      }
    }
  }, [app])

  const toggleIsBeta = () => {
    setApp(current => current ? ({ ...current, isBeta: current.isBeta === '0' ? '1' : '0' }) : null)
  }

  const onRenewalDateChange = (value: Date | null) => {
    if (!value) {
      return
    }
    setRenewalChanged(true)
    setApp(current => current ? ({ ...current, nextRenewal: value.toISOString() }) : null)
  }

  const onChange = (
    key: 'profiles' | 'posts' | 'allowedLocations' | 'stripeId'
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim()
    setApp(current => {
      if (!current) {
        return null
      }
      if (key === 'profiles') {
        return { ...current, profilesLimit: parseInt(value, 10) }
      }
      if (key === 'allowedLocations') {
        return { ...current, allowedLocations: parseInt(value, 10) }
      }
      if (key === 'posts') {
        return { ...current, postsPerDay: parseInt(value, 10) }
      }
      if (key === 'stripeId') {
        return { ...current, stripeSubscriptionId: value }
      }
      return current
    })
  }

  const onTierChange = (value: string) => {
    setApp(current => current ? ({ ...current, tier: parseInt(value, 10) }) : null)
  }

  const onCurrencyChange = (value: string) => {
    const currency = value as AppCurrency
    setApp(current => current ? ({
      ...current,
      currency,
      tier: currency === 'inr' ? INR_TIER : current.tier
    }) : null)
  }

  const onFeatureFlagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    if (name === 'igDirect') {
      setApp(current => current ? ({
        ...current,
        config: { ...current.config, igDirect: checked }
      }) : null)
    }
  }

  const onUpdateApp = () => {
    if (!app) {
      return
    }
    if (app.profilesLimit === undefined && app.postsPerDay === undefined) {
      dispatch(message('Please enter Profiles or Posts limits!'))
      return
    }
    setLoading(true)
    firstValueFrom(dispatch(updateApp(
      app.id,
      app.profilesLimit,
      app.postsPerDay,
      app.allowedLocations,
      app.chargifySubscriptionId,
      app.isBeta,
      app.config?.igDirect === true,
      app.stripeSubscriptionId,
      renewalChanged ? app.nextRenewal : undefined,
      resolveTier(app),
      app.currency ?? DEFAULT_CURRENCY
    )))
      .then((response: any) => {
        setLoading(false)
        if (response.result === 'success') {
          dispatch(message('App updated!'))
          navigate(`/admin/support/apps?appId=${app.id}`)
        } else {
          dispatch(message('App update failed!'))
        }
      })
      .catch(() => {
        setLoading(false)
        dispatch(message('App update failed!'))
      })
  }

  const onBack = () => {
    navigate('/admin/support/apps?appId=' + id)
  }

  const renderSubscriptionField = (value: number | string | null | undefined, format?: (v: any) => string) => {
    if (!app?.stripeSubscriptionId) {
      return 'N/A'
    }
    if (subscription === null) {
      return '-'
    }
    if (value === null || value === undefined || value === '') {
      return 'N/A'
    }
    return format ? format(value) : value
  }

  const tierLocked = (app?.currency ?? DEFAULT_CURRENCY) === 'inr'

  const formatTimestamp = (ts: number) => new Date(ts * TIMESTAMP_MULTIPLIER).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  return (
    <AdminPage title="App Details">
      <section className={styles.container}>
        <div className={styles['details-toolbar']}>
          <Button size="small" onClick={onBack}>← Back to Apps</Button>
        </div>
        {app && (
          <table className={styles['details-table']}>
            <tbody>
              <tr>
                <th>App ID</th>
                <td>{app.id}</td>
              </tr>
              <tr>
                <th>User ID</th>
                <td>{app.userId}</td>
              </tr>
              <tr>
                <th>Profiles limit</th>
                <td>
                  <Input
                    type="number"
                    inputRef={profilesInputRef}
                    classes={{ root: styles['edit-input'] }}
                    onChange={onChange('profiles')}
                  />
                </td>
              </tr>
              <tr>
                <th>Posts per day</th>
                <td>
                  <Input
                    type="number"
                    inputRef={postsInputRef}
                    classes={{ root: styles['edit-input'] }}
                    onChange={onChange('posts')}
                  />
                </td>
              </tr>
              <tr>
                <th>Tier</th>
                <td className={styles['field-with-hint']}>
                  <PPSelect
                    withCaret
                    options={TIER_OPTIONS}
                    selectedValue={String(resolveTier(app))}
                    disabled={tierLocked}
                    onSelectionChange={onTierChange}
                  />
                  {tierLocked && <span className={styles['field-hint']}>{INR_TIER_HINT}</span>}
                </td>
              </tr>
              <tr>
                <th>Currency</th>
                <td className={styles['field-with-hint']}>
                  <PPSelect
                    withCaret
                    options={CURRENCY_OPTIONS}
                    selectedValue={app.currency ?? DEFAULT_CURRENCY}
                    onSelectionChange={onCurrencyChange}
                  />
                  {!tierLocked && <span className={styles['field-hint']}>{INR_TIER_HINT}</span>}
                </td>
              </tr>
              <tr>
                <th>Beta user</th>
                <td>
                  <Checkbox
                    checked={app.isBeta === '1'}
                    color="primary"
                    onChange={toggleIsBeta}
                  />
                </td>
              </tr>
              <tr>
                <th>Stripe ID</th>
                <td>
                  <Input
                    type="text"
                    inputRef={stripeInputRef}
                    classes={{ root: styles['full-width'] }}
                    onChange={onChange('stripeId')}
                  />
                </td>
              </tr>
              <tr>
                <th>Original renewal date</th>
                <td>{renderSubscriptionField(subscription?.currentPeriodEnd, formatTimestamp)}</td>
              </tr>
              {/* <tr>
                <th>Custom renewal date offset (days)</th>
                <td>{renderSubscriptionField(subscription?.currentPeriodEndActualDaysDiff)}</td>
              </tr> */}
              <tr>
                <th>Next renewal date</th>
                <td>
                  {app.stripeSubscriptionId ? (
                    <DatePicker
                      value={app.nextRenewal ? new Date(app.nextRenewal) : null}
                      onChange={onRenewalDateChange}
                    />
                  ) : 'N/A'}
                </td>
              </tr>
              <tr>
                <th>IG Direct</th>
                <td>
                  <Checkbox
                    checked={app.config?.igDirect === true}
                    color="primary"
                    name="igDirect"
                    onChange={onFeatureFlagChange}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {app && (
          <div className={styles['details-actions']}>
            <Button variant="contained" color="primary" onClick={onUpdateApp}>Save</Button>
          </div>
        )}
      </section>
      <Backdrop open={loading} className={styles.backdrop}>
        <CircularProgress color="primary" />
      </Backdrop>
    </AdminPage>
  )
}

export default AppDetailsRoute
